/* Orbis — background video etiquette:
   - visitors with "reduce motion" enabled get paused first frames, not loops
   - videos otherwise rely purely on the passive `autoplay loop muted
     playsinline` attributes for playback — the same rule cosmiq.js already
     follows: never call video.play() from a non-gesture context. A JS-
     initiated play() that gets blocked by autoplay policy (Data Saver mode,
     iOS Low Power Mode, restrictive in-app WebViews) is what makes the
     browser show its native "tap to play" button; a passive attribute
     failing silently does not. This file used to pause videos on scroll-out
     and resume them with .play() on scroll-in, which was exactly that risky
     call — removed, since `loop` already keeps them playing indefinitely
     once started, and browsers already throttle decode work for elements
     scrolled far off screen on their own. */

(function () {
  'use strict';

  function init(root) {
    var videos = (root || document).querySelectorAll('.orbis video');
    if (!videos.length) return;

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    videos.forEach(function (video) {
      if (reduced) {
        video.removeAttribute('autoplay');
        video.pause();
      }
    });

    // iOS Low Power Mode blocks ALL autoplay (even muted) until a real user
    // gesture. Retry once on the first touch/click — play() inside a genuine
    // gesture is always allowed, so the backgrounds recover invisibly instead
    // of sitting frozen. (The native play-button chrome is hidden in CSS.)
    if (!window.__orbisGestureRetry) {
      window.__orbisGestureRetry = true;
      var retry = function () {
        document.querySelectorAll('.orbis video[autoplay], .cosmiq-hero video[autoplay]').forEach(function (video) {
          // Scroll-scrubbed videos are PAUSED ON PURPOSE — their currentTime
          // is driven by scroll position. Playing one here would make it run
          // away from the scrub and fight it frame by frame, so both kinds
          // are excluded: the armed Cosmiq hero and an Orbis hero in
          // scroll-scrub mode.
          if (video.paused && !video.closest('.cosmiq-hero--armed, .orbis-hero--scroll')) {
            video.play().catch(function () {});
          }
        });
        document.removeEventListener('touchstart', retry);
        document.removeEventListener('click', retry);
      };
      document.addEventListener('touchstart', retry, { passive: true, once: true });
      document.addEventListener('click', retry, { once: true });
    }
  }

  /* Site preloader: the overlay stays up until the page's heavy assets are
     GENUINELY ready — not a fake timer. It waits for:
       - the hero video buffered enough to play through without stalling
       - the Cosmiq scroll-video (the heaviest scroll experience) mostly buffered
       - fonts fully loaded (no text flash after reveal)
     The bar shows real combined progress (weighted). A 20s hard cap
     guarantees a terrible network can never hold the page hostage — raised
     from 15s because fully buffering the scroll-scrubbed video (not just
     "playable") genuinely needs more time on slow connections. */
  function initLoaders(root) {
    // Guard against running twice: this script is included once per Orbis
    // section, so on a page with several Orbis sections its top-level code
    // executes multiple times — without this guard, the hero loader would
    // get initialized redundantly for every include.
    if (window.__orbisLoadersBooted) return;
    window.__orbisLoadersBooted = true;

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var loaders = (root || document).querySelectorAll('[data-orbis-loader]');
    loaders.forEach(function (loader) {
      var wrapper = loader.parentElement;
      var video = wrapper ? wrapper.querySelector('.orbis-hero__video') : null;
      var textEl = loader.querySelector('[data-orbis-loader-text]');
      var barEl = loader.querySelector('[data-orbis-loader-bar]');
      if (!video) {
        loader.classList.add('is-hidden');
        return;
      }

      // A scroll-scrubbed hero has the same "can jump to ANY point in the
      // timeline instantly" problem as the Cosmiq video, so it has to be
      // buffered end to end before the loader lets go — "can play through
      // from here" only guarantees sequential playback, which is not what
      // scrubbing does.
      var heroScrubbed = !!(wrapper && wrapper.classList.contains('orbis-hero--scroll'));

      // Quote and author both fade in via pure CSS — no JS text animation.

      // Secondary heavy asset: the scroll-scrubbed Cosmiq video (may not
      // exist on every page — weights adapt automatically).
      var cosmiqVideo = document.querySelector('.cosmiq-hero__video');

      var fontsReady = false;
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () { fontsReady = true; });
        // Fonts rarely take long; don't let a hung font API block forever.
        setTimeout(function () { fontsReady = true; }, 5000);
      } else {
        fontsReady = true;
      }

      var hidden = false;
      var forced = false;
      var rafId = null;
      var displayPct = 0;
      var cosmiqDropped = false;
      var lastCosmiqPct = -1;
      var lastCosmiqChange = performance.now();
      // Minimum on-screen time for the loader (even when everything is
      // instantly ready) so the quote can actually be read.
      var shownAt = performance.now();
      var MIN_SHOW_MS = 2000;

      function hide() {
        if (hidden) return;
        hidden = true;
        if (rafId) cancelAnimationFrame(rafId);
        loader.classList.add('is-hidden');
      }

      function videoPct(v, opts) {
        if (!v) return 100;
        if (v.error) return 100;
        if (opts.requireFullBuffer) {
          // This video is scroll-scrubbed: scrolling can jump to ANY point
          // in its timeline instantly. "Can play through from here" isn't
          // enough — the whole file needs to be buffered, or a fast scroll
          // right after the loader hides can land on an unbuffered stretch
          // and stall (the bug reports of animation not being fully ready).
          if (v.duration && v.buffered.length) {
            var covered = v.buffered.end(v.buffered.length - 1) / v.duration;
            if (covered >= 0.97) return 100;
            return Math.min(99, covered * 100);
          }
          return 0;
        }
        if (v.readyState >= opts.fullyAt) return 100;
        if (v.duration && v.buffered.length) {
          return Math.min(99, (v.buffered.end(v.buffered.length - 1) / v.duration) * 100);
        }
        return 0;
      }

      function combinedPct() {
        if (forced) return 100;
        // Weights: hero video is what the visitor sees first; the Cosmiq
        // video is what must be smooth on scroll; fonts are quick but
        // matter for the reveal.
        var hero = heroScrubbed
          ? videoPct(video, { requireFullBuffer: true })                     // scrubbed: any frame reachable at any moment
          : videoPct(video, { fullyAt: 4 });                                 // looping: play-through is enough (sequential)
        var cosmiq = (cosmiqVideo && !cosmiqDropped) ? videoPct(cosmiqVideo, { requireFullBuffer: true }) : 100;
        var fonts = fontsReady ? 100 : 0;

        // Stall detector: some phones (Data Saver etc.) refuse to preload
        // the below-fold Cosmiq video at all. If its buffering makes zero
        // progress for 6s while the hero is done, stop waiting for it — it
        // will buffer on scroll like before, just without the guarantee.
        if (cosmiqVideo && !cosmiqDropped) {
          if (cosmiq !== lastCosmiqPct) {
            lastCosmiqPct = cosmiq;
            lastCosmiqChange = performance.now();
          } else if (hero >= 100 && performance.now() - lastCosmiqChange > 6000) {
            cosmiqDropped = true;
            cosmiq = 100;
          }
        }

        return hero * 0.55 + cosmiq * 0.3 + fonts * 0.15;
      }

      function render() {
        var rounded = Math.round(displayPct);
        if (textEl) textEl.textContent = 'Loading... ' + rounded + '%';
        if (barEl) barEl.style.width = displayPct + '%';
      }

      function tick() {
        var target = combinedPct();

        // Hold just short of done until the minimum display time has
        // passed — gives the quote time to be read even on instant loads.
        if (performance.now() - shownAt < MIN_SHOW_MS) {
          target = Math.min(target, 97);
        }

        if (reduced) {
          displayPct = target;
        } else {
          displayPct += (target - displayPct) * 0.12;
          if (target - displayPct < 0.5) displayPct = target;
        }
        render();

        if (displayPct >= 100) {
          hide();
          return;
        }
        rafId = requestAnimationFrame(tick);
      }

      video.addEventListener('error', function () { forced = true; });

      // Hard cap: never hold the page hostage to a bad network.
      setTimeout(function () { forced = true; }, 20000);

      rafId = requestAnimationFrame(tick);
    });
  }

  function boot(root) {
    init(root);
    initLoaders(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
})();
